/**
 * T-Row Orchestrator
 *
 * Coordinates rendering of T-hangar row buildings.
 * Uses T-shaped unit composer for each unit in the row.
 *
 * Handles building-level concerns:
 * - Unified rectangular floor (no seams between per-unit T-shaped floors)
 * - End-cap walls (flat rectangular walls instead of exposed T-profile)
 * - Edge suppression for internal end-unit Z-shapes
 */

import * as THREE from 'three'
import { FEET_TO_METERS } from '@/constants/units'
import type { GeneratedBuilding, GeneratedUnit } from '@/lib/templates/types'
import { renderTUnit, type TUnitRenderOptions } from '../composers/TShapedUnitComposer'
import { renderFloor } from '../primitives/FloorRenderer'
import { createWallMaterial, applyShadowSettings } from '../utils/materials'

/**
 * Building render options for T-hangar rows
 */
export interface TRowRenderOptions extends TUnitRenderOptions {
  buildingId?: string
  buildingName?: string
  position?: [number, number, number]
  rotationDeg?: number
}

/**
 * Render a T-hangar row building from generated config
 */
export function renderTRowBuilding(
  building: GeneratedBuilding,
  units: GeneratedUnit[],
  options: TRowRenderOptions = {}
): THREE.Group {
  const {
    buildingId,
    buildingName,
    position = [0, 0, 0],
    rotationDeg,
    ...unitOptions
  } = options

  const group = new THREE.Group()
  group.name = `building-${buildingId || building.code}`
  group.userData = {
    buildingId: buildingId || building.code,
    buildingName: buildingName || building.name,
    buildingType: building.type,
    template: 'standard_t_hangar',
    unitCount: units.length,
  }

  // Get colors from building settings
  const floorColor = building.settings?.floor_color || unitOptions.floorColor
  const wallColor = building.settings?.wall_color || unitOptions.wallColor
  const wallThk = (building.type_config?.wall_thickness_ft as number) ?? 0.5

  // Create a group for all units
  const unitsGroup = new THREE.Group()
  unitsGroup.name = 'units'

  // ── Fix 1: Unified building floor ──────────────────────────────────────
  // One rectangular PlaneGeometry for the entire building eliminates seams
  // between per-unit T-shaped floor meshes.
  const buildingFloor = renderFloor(
    { width: building.exterior_width, depth: building.exterior_depth },
    { color: floorColor ?? undefined, enableShadows: true }
  )
  // renderFloor centers on origin; shift to building's X center
  buildingFloor.position.x = (building.exterior_width / 2) * FEET_TO_METERS
  buildingFloor.name = 'building-floor'
  unitsGroup.add(buildingFloor)

  // ── Fix 2: Suppress Z-shaped end edges ─────────────────────────────────
  // First unit's left Z-edges and last unit's right Z-edges would expose the
  // T-profile externally. Mark them 'internal' so shouldRenderTHangarEdge()
  // returns false, then add flat end-cap walls below.
  const unitsCopy = units.map((u, i) => {
    if (i !== 0 && i !== units.length - 1) return u
    if (!u.tHangarEdges) return u

    const patchedEdges = u.tHangarEdges.map(edge => {
      // First unit: suppress left Z-edges
      if (i === 0 && (edge.id === 'left_wing' || edge.id === 'left_step' || edge.id === 'left_tail')) {
        return { ...edge, type: 'internal' as const }
      }
      // Last unit: suppress right Z-edges
      if (i === units.length - 1 && (edge.id === 'right_wing' || edge.id === 'right_step' || edge.id === 'right_tail')) {
        return { ...edge, type: 'internal' as const }
      }
      return edge
    })

    return { ...u, tHangarEdges: patchedEdges }
  })

  // Render each unit (with skipFloor since we have a unified floor)
  for (let i = 0; i < unitsCopy.length; i++) {
    const unitGroup = renderTUnit(unitsCopy[i], i, {
      ...unitOptions,
      floorColor,
      wallColor,
      wallThickness: wallThk,
      skipFloor: true,
    })
    unitsGroup.add(unitGroup)
  }

  // ── Fix 2 (cont): End-cap walls ───────────────────────────────────────
  // Two flat rectangular walls covering the full building depth × height
  // at the left and right edges of the building.
  const buildingHeight = building.exterior_height
  const buildingDepth = building.exterior_depth
  const endCapMat = createWallMaterial({ color: wallColor ?? undefined })

  // Left end-cap wall (X = wallThk/2)
  const leftCap = new THREE.Mesh(
    new THREE.BoxGeometry(
      wallThk * FEET_TO_METERS,
      buildingHeight * FEET_TO_METERS,
      buildingDepth * FEET_TO_METERS
    ),
    endCapMat
  )
  leftCap.position.set(
    (wallThk / 2) * FEET_TO_METERS,
    (buildingHeight / 2) * FEET_TO_METERS,
    0
  )
  leftCap.name = 'endcap-left'
  applyShadowSettings(leftCap, true, true)
  unitsGroup.add(leftCap)

  // Right end-cap wall (X = exterior_width - wallThk/2)
  const rightCap = new THREE.Mesh(
    new THREE.BoxGeometry(
      wallThk * FEET_TO_METERS,
      buildingHeight * FEET_TO_METERS,
      buildingDepth * FEET_TO_METERS
    ),
    endCapMat
  )
  rightCap.position.set(
    (building.exterior_width - wallThk / 2) * FEET_TO_METERS,
    (buildingHeight / 2) * FEET_TO_METERS,
    0
  )
  rightCap.name = 'endcap-right'
  applyShadowSettings(rightCap, true, true)
  unitsGroup.add(rightCap)

  // ── Fix 2 (cont): Back-wall gap fillers for end units ──────────────────
  // The back wall is only tail_width wide, but end units need the back wall
  // to extend to the end-cap. Add a filler segment on the outer side.
  const unitWidth = (building.type_config?.unit_width_ft as number) ?? 44.5
  const tailWidth = (building.type_config?.tail_width_ft as number) ?? 22.5
  const wingDepth = (building.type_config?.wing_depth_ft as number) ?? 21
  const stepWidth = (unitWidth - tailWidth) / 2
  const halfTail = tailWidth / 2

  if (units.length > 0 && stepWidth > 0) {
    // Back wall Z in unit-local coords: f * -(wingDepth + wallThk/2)
    // where f = south ? 1 : -1

    // First unit (index 0, south-facing): gap on the left side of the back wall
    const firstUnit = units[0]
    const firstCenterX = firstUnit.offset_x + unitWidth / 2
    const firstFacing = 1 // south-facing (index 0 is always even)
    const firstBackZ = firstFacing * -(wingDepth + wallThk / 2)
    const firstFiller = new THREE.Mesh(
      new THREE.BoxGeometry(
        stepWidth * FEET_TO_METERS,
        buildingHeight * FEET_TO_METERS,
        wallThk * FEET_TO_METERS
      ),
      endCapMat
    )
    firstFiller.position.set(
      (firstCenterX - halfTail - stepWidth / 2) * FEET_TO_METERS,
      (buildingHeight / 2) * FEET_TO_METERS,
      firstBackZ * FEET_TO_METERS
    )
    firstFiller.name = 'backwall-filler-left'
    applyShadowSettings(firstFiller, true, true)
    unitsGroup.add(firstFiller)

    // Last unit: gap on the right side of the back wall
    const lastIdx = units.length - 1
    const lastUnit = units[lastIdx]
    const lastCenterX = lastUnit.offset_x + unitWidth / 2
    const lastFacing = lastIdx % 2 === 0 ? 1 : -1
    const lastBackZ = lastFacing * -(wingDepth + wallThk / 2)
    const lastFiller = new THREE.Mesh(
      new THREE.BoxGeometry(
        stepWidth * FEET_TO_METERS,
        buildingHeight * FEET_TO_METERS,
        wallThk * FEET_TO_METERS
      ),
      endCapMat
    )
    lastFiller.position.set(
      (lastCenterX + halfTail + stepWidth / 2) * FEET_TO_METERS,
      (buildingHeight / 2) * FEET_TO_METERS,
      lastBackZ * FEET_TO_METERS
    )
    lastFiller.name = 'backwall-filler-right'
    applyShadowSettings(lastFiller, true, true)
    unitsGroup.add(lastFiller)
  }

  // Center units on building origin (units positioned from west edge)
  const buildingCenterX = building.exterior_width / 2
  unitsGroup.position.x = -buildingCenterX * FEET_TO_METERS

  group.add(unitsGroup)

  // Apply building rotation
  const rotation = rotationDeg ?? (building.rotation * 180 / Math.PI)
  if (rotation !== 0) {
    group.rotation.y = (rotation * Math.PI) / 180
  }

  // Apply building position (convert feet to meters)
  group.position.set(
    position[0] * FEET_TO_METERS,
    position[1] * FEET_TO_METERS,
    position[2] * FEET_TO_METERS
  )

  return group
}
